// Per-key Zod schemas for `settings_kv` config blobs.
// Phase 2.3 of v2 redesign — closes Phase 1.6's deferred guard removal by
// extending the API boundary to cover the config blobs returned by
// `/api/config` and `/api/config/<key>`.
//
// Each schema mirrors the matching interface declaration in `app/src/config.ts`.
// `.passthrough()` is used liberally so legacy admin-saved blobs that carry
// extra fields don't fail to parse — we forward unknown keys through rather
// than rejecting them. Required fields are still enforced.
//
// Drift between these schemas and `config-defaults.ts` literals is caught by
// the drift test in `app/src/test/schemas/drift.test.ts` (Phase 2.5).

import { z } from 'zod';

// ─── Shared sub-schemas ──────────────────────────────────────

const navLinkSchema = z.object({
  label: z.string(),
  href: z.string(),
});

const sectionHeaderFieldsSchema = {
  heading: z.string().optional(),
  body: z.string().optional(),
  visible: z.boolean().optional(),
};

const aboutStatSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const aboutImageSchema = z.object({
  src: z.string(),
  alt: z.string(),
});

const serviceItemSchema = z.object({
  iconName: z.string(),
  title: z.string(),
  description: z.string(),
  image: z.string(),
  deliverables: z.array(z.string()).optional(),
  bestFor: z.string().optional(),
}).passthrough();

const portfolioProjectSchema = z.object({
  slug: z.string(),
  title: z.string(),
  tag: z.string(),
  outcome: z.string(),
  associatedWith: z.string().optional(),
  description: z.string(),
}).passthrough();

const testimonialSchema = z.object({
  name: z.string(),
  title: z.string(),
  quote: z.string(),
  image: z.string(),
}).passthrough();

const speakingEngagementSchema = z.object({
  event: z.string(),
  role: z.string(),
  year: z.string(),
}).passthrough();

const methodStepSchema = z.object({
  key: z.string(),
  title: z.string(),
  description: z.string(),
}).passthrough();

const audiencePathSchema = z.object({
  iconName: z.string(),
  title: z.string(),
  description: z.string(),
  ctaLabel: z.string(),
  ctaHref: z.string(),
}).passthrough();

const footerLinkColumnSchema = z.object({
  title: z.string(),
  links: z.array(navLinkSchema),
}).passthrough();

const socialLinkInlineSchema = z.object({
  iconName: z.string(),
  href: z.string(),
  label: z.string(),
}).passthrough();

const seoJsonLdSchema = z.object({
  name: z.string().optional(),
  alternateName: z.string().optional(),
  jobTitle: z.string().optional(),
  description: z.string().optional(),
  url: z.string().optional(),
  image: z.string().optional(),
  sameAs: z.array(z.string()).optional(),
  // ProfessionalService graph node (additive — optional with code-side
  // fallbacks in server/lib/seoTemplate.ts, so legacy rows need no migration).
  serviceTypes: z.array(z.string()).optional(),
  areaServed: z.string().optional(),
}).passthrough();

// ─── Top-level config blob schemas (one per settings_kv key) ─

/** `settings_kv.site_config` */
export const siteConfigSchema = z.object({
  language: z.string(),
  title: z.string(),
  description: z.string(),
}).passthrough();

/** `settings_kv.nav_config` */
export const navigationConfigSchema = z.object({
  logo: z.string(),
  links: z.array(navLinkSchema),
  contactLabel: z.string(),
  /** Where the header CTA points. Optional so blobs persisted before Phase 2
   *  (when this key was stripped on every boot — see migrateAuditShapeOnce's
   *  HISTORY note) still parse under .strict(). */
  contactHref: z.string().optional(),
}).strict();

/** `settings_kv.hero_config`
 *
 * Strict shape — mirrors what `Hero.tsx` actually renders. Legacy fields
 * (`heading`, `body`, `roles[]`) were dropped post-`e8ac945`;
 * the one-shot `migrateHeroConfigOnce()` in `server/lib/seed.ts` collapses
 * existing rows into this shape on first boot.
 */
export const heroConfigSchema = z.object({
  visible: z.boolean(),
  name: z.string(),
  role: z.string(),
  headline: z.string(),
  description: z.string(),
  primaryCtaLabel: z.string(),
  primaryCtaHref: z.string(),
  secondaryCtaLabel: z.string(),
  secondaryCtaHref: z.string(),
  positioningLine: z.string(),
  backgroundImage: z.string(),
  // Tunable layout — added in the alignment rewrite. Defaults supplied by
  // `config-defaults.ts`; `migrateHeroConfigOnce()` backfills existing rows.
  headlineMaxWidth: z.number(),
  // Vertical focal point of the background image (CSS percentage string).
  portraitObjectPositionY: z.string(),
  // ── Hero v4 — dual-mode hero with scrim + tunable lens ─────────
  // Render path. 'lens' = sharp viewfinder window over a blurred backdrop
  // (the existing design). 'scrim' = single blurred backdrop with a left
  // gradient scrim creating a quiet canvas for the text.
  heroMode: z.enum(['lens', 'scrim']),
  // Horizontal focal point of the bg image (0–100, CSS %).
  backgroundPositionX: z.number(),
  // Backdrop blur radius in px.
  backgroundBlurPx: z.number(),
  // Scrim — only rendered in scrim mode.
  overlayEnabled: z.boolean(),
  overlayDirection: z.enum(['left-right', 'right-left', 'top-bottom', 'radial']),
  overlayOpacityLight: z.number(),
  overlayOpacityDark: z.number(),
  overlayColorLight: z.string(),
  overlayColorDark: z.string(),
  // Hero strong-text color per theme. Resolved at runtime via [data-theme].
  textColorLight: z.string(),
  textColorDark: z.string(),
  // Lens window — position + size, each as % (X/Y of section, W/H as vw).
  // Replace the prior `lensSizePercent` (migrated into W = H = old value).
  lensPositionXPercent: z.number(),
  lensPositionYPercent: z.number(),
  lensWidthPercent: z.number(),
  lensHeightPercent: z.number(),
  // Background image transform. bgImageOffsetX/Y override backgroundPositionX
  // / portraitObjectPositionY in Hero.tsx when present; bgImageScale drives
  // `transform: scale()` applied to both <img> layers. Backfilled by
  // `migrateHeroBackgroundOnce()`.
  bgImageOffsetX: z.number(),
  bgImageOffsetY: z.number(),
  bgImageScale: z.number(),
}).strict();

/** `settings_kv.about_config` — note: career timeline data lives in the
 *  `experience` table now, not in this blob. The admin editor at
 *  `/admin/about-config` reflects this with a readonly note pointing to the
 *  Experience manager. */
export const aboutConfigSchema = z.object({
  ...sectionHeaderFieldsSchema,
  label: z.string(),
  description: z.string(),
  experienceValue: z.string(),
  experienceLabel: z.string(),
  portraitImage: z.string().optional(),
  quote: z.string().optional(),
  tags: z.array(z.string()).optional(),
  stats: z.array(aboutStatSchema),
  images: z.array(aboutImageSchema),
  // Per-surface portrait overrides. When set, take precedence over
  // `portraitImage` on the matching surface only. Leave blank to fall
  // back to the shared portraitImage above.
  portraitImageHome: z.string().optional(),
  portraitImageAbout: z.string().optional(),
  // Per-surface element-visibility flags. Absent / undefined means "show".
  // An admin who never touches these sees today's behavior on both surfaces.
  showOnHome: z.object({
    label: z.boolean().optional(),
    heading: z.boolean().optional(),
    body: z.boolean().optional(),
    quote: z.boolean().optional(),
    tags: z.boolean().optional(),
    stats: z.boolean().optional(),
    experienceBadge: z.boolean().optional(),
    portrait: z.boolean().optional(),
  }).optional(),
  showOnAbout: z.object({
    label: z.boolean().optional(),
    heading: z.boolean().optional(),
    description: z.boolean().optional(),
    body: z.boolean().optional(),
    quote: z.boolean().optional(),
    tags: z.boolean().optional(),
    stats: z.boolean().optional(),
    portrait: z.boolean().optional(),
    timeline: z.boolean().optional(),
  }).optional(),
}).strict();

/** `settings_kv.services_heading` — note: full services list lives in the
 *  `services` table, not in this KV blob. Only label + heading here. */
export const servicesHeadingSchema = z.object({
  label: z.string(),
  heading: z.string(),
}).passthrough();

/** Full servicesConfig shape used by the config-defaults fallback. */
export const servicesConfigSchema = z.object({
  ...sectionHeaderFieldsSchema,
  label: z.string(),
  heading: z.string(),
  services: z.array(serviceItemSchema),
}).passthrough();

/** portfolioConfig fallback shape — `projects` table is canonical at runtime. */
export const portfolioConfigSchema = z.object({
  projects: z.array(portfolioProjectSchema),
}).passthrough();

/** testimonialsConfig fallback shape — `testimonials` table is canonical. */
export const testimonialsConfigSchema = z.object({
  testimonials: z.array(testimonialSchema),
}).passthrough();

/** speakingConfig fallback. `engagements` seeds the `speaking_events` table.
 *  The legacy `speaking_topics` settings_kv blob was removed in the
 *  2026-05-26 audit fix batch (no frontend reader; no admin form). */
export const speakingConfigSchema = z.object({
  topics: z.array(z.string()),
  engagements: z.array(speakingEngagementSchema),
}).passthrough();

/** `settings_kv.cta_config` */
export const ctaConfigSchema = z.object({
  ...sectionHeaderFieldsSchema,
  tags: z.array(z.string()),
  heading: z.string(),
  description: z.string(),
  buttonText: z.string(),
  buttonHref: z.string(),
  email: z.string(),
  backgroundImage: z.string(),
}).strict();

/** `settings_kv.newsletter_nudge` */
export const newsletterNudgeSchema = z.object({
  enabled: z.boolean(),
  heading: z.string(),
  body: z.string(),
  ctaLabel: z.string(),
}).strict();

/** `settings_kv.expertise_config` */
export const expertiseConfigSchema = z.object({
  labels: z.array(z.string()),
}).passthrough();

/** `settings_kv.method_config` */
export const methodConfigSchema = z.object({
  label: z.string(),
  heading: z.string(),
  intro: z.string(),
  frameworkName: z.string(),
  steps: z.array(methodStepSchema),
}).strict();

/** `settings_kv.audience_paths_config` */
/** `settings_kv.problem_grid` — the homepage "What I help solve" block. */
const problemGridItemSchema = z.object({
  iconName: z.string(),
  title: z.string(),
  description: z.string(),
  href: z.string().optional(),
  // Not optional: a tile whose specialist page is unpublished falls back to
  // this, so without it the tile would have nowhere to go.
  fallbackHref: z.string(),
}).passthrough();

export const problemGridSchema = z.object({
  label: z.string(),
  heading: z.string(),
  visible: z.boolean().optional(),
  items: z.array(problemGridItemSchema),
}).strict();

export const audiencePathsConfigSchema = z.object({
  label: z.string(),
  heading: z.string(),
  paths: z.array(audiencePathSchema),
}).strict();

/**
 * `settings_kv.topic_clusters` — the six insight hubs at
 * `/insights/topics/<slug>`.
 *
 * `tags[]` IS the reverse map: an article belongs to a cluster when it carries
 * one of these tag strings. Tags never appear in a URL, so no slugify and no
 * lossy round-trip — `slug` is authored and is the only thing routing sees.
 */
const topicClusterSchema = z.object({
  // Authored URL segment. Kept kebab-case so the route stays predictable and
  // an admin cannot produce a slug that needs escaping.
  slug: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'lowercase words separated by single hyphens'),
  name: z.string(),
  title: z.string(),
  description: z.string(),
  // Article tag display strings, verbatim — "Platform Thinking", not a slug.
  tags: z.array(z.string()),
  // Service-page path or /contact?offer=<slug>. Optional because a cluster may
  // have no destination yet; useLiveTarget gates whichever is set.
  ctaHref: z.string().optional(),
}).passthrough();

export const topicClustersSchema = z.object({
  label: z.string(),
  heading: z.string(),
  // Gates ONLY the "Browse by topic" row on /insights. The hub routes
  // themselves are gated by article count, never by this flag.
  visible: z.boolean().optional(),
  clusters: z.array(topicClusterSchema),
}).strict()
  .refine(
    (cfg) => new Set(cfg.clusters.map((c) => c.slug)).size === cfg.clusters.length,
    { message: 'cluster slugs must be unique — two clusters cannot share a URL', path: ['clusters'] },
  )
  .refine(
    (cfg) => {
      const seen = new Set<string>();
      for (const c of cfg.clusters) {
        for (const t of c.tags) {
          if (seen.has(t)) return false;
          seen.add(t);
        }
      }
      return true;
    },
    // clusterForTag() returns the first match, so a tag in two clusters would
    // silently pick one and make the article sidebar non-deterministic.
    { message: 'a tag may belong to only one cluster', path: ['clusters'] },
  );

/** `settings_kv.book_config` */
export const bookConfigSchema = z.object({
  enabled: z.boolean(),
  title: z.string(),
  coverImage: z.string(),
  description: z.string(),
  ctaLabel: z.string(),
  ctaHref: z.string(),
}).strict();

/** `settings_kv.seo_config` */
export const seoConfigSchema = z.object({
  ogTitle: z.string(),
  ogDescription: z.string(),
  ogImage: z.string(),
  ogUrl: z.string().optional(),
  twitterCard: z.string(),
  twitterSite: z.string().optional(),
  jsonLd: seoJsonLdSchema,
}).strict();

/** `settings_kv.error_tracking_config` — public blob (DSN must reach the
 *  browser). Tolerant on purpose: read very early (raw localStorage + /api/config)
 *  and must never fail-closed on a legacy/disabled row. The auth token is NOT
 *  part of this blob — it is an env-only server secret. */
export const errorTrackingConfigSchema = z.object({
  enabled: z.boolean().optional(),
  dsn: z.string().optional(),
  serverDsn: z.string().optional(),
  heartbeatUrl: z.string().optional(),
  environment: z.string().optional(),
  enableLogs: z.boolean().optional(),
  tracesSampleRate: z.number().min(0).max(1).optional(),
}).passthrough();

/** `settings_kv.footer_config` */
export const footerConfigSchema = z.object({
  logo: z.string(),
  description: z.string(),
  columns: z.array(footerLinkColumnSchema),
  socialLinks: z.array(socialLinkInlineSchema).optional(),
  showSocialRow: z.boolean().optional(),
  copyright: z.string(),
  credit: z.string(),
}).strict();

/** `settings_kv.legal_config` — Privacy page (and future Terms). Body is
 *  Markdown rendered via react-markdown on the public surface. */
export const legalConfigSchema = z.object({
  heading: z.string().optional(),
  body: z.string().optional(),
  visible: z.boolean().optional(),
}).strict();

/** `settings_kv.offers_section` — heading + intro for the Engagement Models
 *  block on /services. Items themselves live in the `offers` table. */
export const offersSectionSchema = z.object({
  heading: z.string().optional(),
  intro: z.string().optional(),
  visible: z.boolean().optional(),
}).strict();

/** `settings_kv.insights_section` — heading + intro for the /insights page
 *  banner. Article cards themselves live in the `articles` table. */
export const insightsSectionSchema = z.object({
  heading: z.string().optional(),
  intro: z.string().optional(),
  visible: z.boolean().optional(),
}).strict();

/** `settings_kv.latest_insights_section` — wrapper copy for the Home
 *  `LatestInsights` block (NOT the /insights page banner). Article cards
 *  themselves live in the `articles` table. */
export const latestInsightsSectionSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().optional(),
  subheading: z.string().optional(),
  ctaLabel: z.string().optional(),
  visible: z.boolean().optional(),
}).strict();

/** Shared shape for the small `*_section` blobs that sit above their
 *  collection tables (projects, testimonials, experience, certifications,
 *  thought_leadership). Managed via SectionCopyPanel; consumed by each
 *  section file via useConfig. */
export const sectionHeaderBlobSchema = z.object({
  heading: z.string().optional(),
  body: z.string().optional(),
  visible: z.boolean().optional(),
}).strict();

/** `settings_kv.speaking_section` — full content for the Home `MediaSpeaking`
 *  block (Three & Company podcast + On-Stage speaking pitch + CTA). The
 *  events list itself comes from the `speaking_events` collection, not from
 *  this blob. */
export const speakingSectionSchema = z.object({
  heading: z.string().optional(),
  body: z.string().optional(),
  visible: z.boolean().optional(),
  podcastLabel: z.string().optional(),
  podcastName: z.string().optional(),
  podcastDescription: z.string().optional(),
  podcastTopics: z.string().optional(),
  podcastPlatforms: z
    .array(z.object({ name: z.string().min(1), url: z.string().optional() }))
    .optional(),
  speakingLabel: z.string().optional(),
  speakingName: z.string().optional(),
  speakingDescription: z.string().optional(),
  ctaLabel: z.string().optional(),
  ctaHref: z.string().optional(),
}).strict();

/** Shared shape for page-level banner blobs (services_page, portfolio_page,
 *  speaking_page). Heading + intro + visibility for the H1 banner area of a
 *  full route's top-of-page. Item lists below live in their own tables. */
export const pageBannerSchema = z.object({
  heading: z.string().optional(),
  intro: z.string().optional(),
  visible: z.boolean().optional(),
}).strict();

/** `settings_kv.speaking_page` — the /workshops banner, plus the workshop
 *  formats and topics the spec asks that page to publish.
 *
 *  Extends the shared banner rather than introducing a new blob: /workshops is
 *  a rename of /speaking, not a new page, and one extra key-pair here costs a
 *  six-surface change instead of two full 11-touchpoint blob registrations.
 *
 *  Emphatically NOT the old `speaking_topics` key — that had no schema, no
 *  reader and no editor, and `migrateAuditOrphansOnce` deletes it on sight. */
export const workshopsPageSchema = z.object({
  heading: z.string().optional(),
  intro: z.string().optional(),
  visible: z.boolean().optional(),
  formats: z.array(z.object({
    name: z.string(),
    duration: z.string().optional(),
    audience: z.string().optional(),
    outcome: z.string().optional(),
  })).optional(),
  topics: z.array(z.string()).optional(),
}).strict();

/** `settings_kv.route_seo` — per-route <title> + meta description. Read by
 *  the server SEO template (crawler-visible <head>) and by useDocumentMeta
 *  (post-hydration), so both layers resolve from one row. */
export const routeSeoSchema = z.object({
  routes: z.array(z.object({
    path: z.string().min(1),
    title: z.string().optional(),
    description: z.string().optional(),
  })),
}).strict();

/** `settings_kv.contact_page` — extends page banner with form labels,
 *  placeholders, engagement-type enum, and direct contact line. */
export const contactPageSchema = z.object({
  heading: z.string().optional(),
  intro: z.string().optional(),
  visible: z.boolean().optional(),
  directContactLine: z.string().optional(),
  formLabels: z.object({
    name: z.string().optional(),
    email: z.string().optional(),
    organisation: z.string().optional(),
    phone: z.string().optional(),
    type: z.string().optional(),
    role: z.string().optional(),
    organisationType: z.string().optional(),
    countryOrMarkets: z.string().optional(),
    desiredTiming: z.string().optional(),
    consent: z.string().optional(),
    message: z.string().optional(),
    submit: z.string().optional(),
  }).optional(),
  formPlaceholders: z.object({
    name: z.string().optional(),
    email: z.string().optional(),
    organisation: z.string().optional(),
    phone: z.string().optional(),
    role: z.string().optional(),
    countryOrMarkets: z.string().optional(),
    desiredTiming: z.string().optional(),
    message: z.string().optional(),
  }).optional(),
  engagementTypes: z.array(z.string()).optional(),
  organisationTypes: z.array(z.string()).optional(),
  privacyLinkLabel: z.string().optional(),
}).strict();

// ─── Per-key map for siteData.fetchConfigMap() (Phase 2.4) ──

/**
 * Maps a `settings_kv.key` value to the Zod schema that should parse the
 * stored value. siteData.fetchConfigMap() walks this on response.
 */
export const configBlobSchemas: Record<string, z.ZodType<unknown>> = {
  site_config: siteConfigSchema,
  nav_config: navigationConfigSchema,
  hero_config: heroConfigSchema,
  about_config: aboutConfigSchema,
  services_heading: servicesHeadingSchema,
  cta_config: ctaConfigSchema,
  newsletter_nudge: newsletterNudgeSchema,
  expertise_config: expertiseConfigSchema,
  method_config: methodConfigSchema,
  audience_paths_config: audiencePathsConfigSchema,
  problem_grid: problemGridSchema,
  topic_clusters: topicClustersSchema,
  book_config: bookConfigSchema,
  seo_config: seoConfigSchema,
  route_seo: routeSeoSchema,
  footer_config: footerConfigSchema,
  legal_config: legalConfigSchema,
  offers_section: offersSectionSchema,
  insights_section: insightsSectionSchema,
  latest_insights_section: latestInsightsSectionSchema,
  speaking_section: speakingSectionSchema,
  projects_section: sectionHeaderBlobSchema,
  testimonials_section: sectionHeaderBlobSchema,
  experience_section: sectionHeaderBlobSchema,
  certifications_section: sectionHeaderBlobSchema,
  thought_leadership_section: sectionHeaderBlobSchema,
  services_page: pageBannerSchema,
  portfolio_page: pageBannerSchema,
  speaking_page: workshopsPageSchema,
  contact_page: contactPageSchema,
  error_tracking_config: errorTrackingConfigSchema,
};
